import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { getUnsubscribeInfo, unsubscribeByToken } from '../../api/Api';
import GlowCard from '../../shared/GlowCard';
import AuthBrandHeader from '../../shared/AuthBrandHeader';
import { useColors } from '../../theme/ColorModeContext';

type PageState = 'loading' | 'confirm' | 'already' | 'done' | 'invalid';

/**
 * Public unsubscribe landing page, deliberately reachable without a login.
 *
 * Loading the page only *reads* the token: mail scanners and antivirus link
 * prefetchers follow GET links, and one of them silently opting a user out would
 * be indistinguishable from the user doing it. The opt-out happens on the button,
 * which POSTs. Mail clients that support RFC 8058 one-click never land here at
 * all; they POST to the API directly from the inbox.
 */
const Unsubscribe = () => {
    const colors = useColors();
    const { token } = useParams();

    const [state, setState] = useState<PageState>('loading');
    const [emailMasked, setEmailMasked] = useState('');
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            const data = await getUnsubscribeInfo(token);
            if (!data || !data.valid) {
                setState('invalid');
                return;
            }
            setEmailMasked(data.email_masked || '');
            setState(data.subscribed ? 'confirm' : 'already');
        };
        fetchData();
    }, [token]);

    const handleUnsubscribe = async () => {
        setProcessing(true);
        const result = await unsubscribeByToken(token);
        setProcessing(false);
        setState(result ? 'done' : 'invalid');
    };

    const body = () => {
        switch (state) {
            case 'loading':
                return <CircularProgress size={32} />;

            case 'invalid':
                return (
                    <>
                        <Typography variant="h5" sx={{ fontFamily: '"Orbitron", sans-serif', marginBottom: '12px' }}>
                            Link not valid
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                            This unsubscribe link is not valid or has already been replaced. You can
                            change your email preferences from your account settings at any time.
                        </Typography>
                    </>
                );

            case 'already':
                return (
                    <>
                        <Typography variant="h5" sx={{ fontFamily: '"Orbitron", sans-serif', marginBottom: '12px' }}>
                            Already unsubscribed
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                            {emailMasked || 'This address'} is not receiving product updates from Web3Pwn.
                        </Typography>
                    </>
                );

            case 'done':
                return (
                    <>
                        <Typography variant="h5" sx={{ fontFamily: '"Orbitron", sans-serif', marginBottom: '12px' }}>
                            Unsubscribed
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                            {emailMasked || 'This address'} will no longer receive product updates.
                            Account and security emails are part of your account and still apply.
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.text.muted, marginTop: '16px' }}>
                            Changed your mind? You can turn product updates back on in{' '}
                            <Link to="/settings" style={{ color: colors.accent.cyan, textDecoration: 'none' }}>
                                Settings
                            </Link>.
                        </Typography>
                    </>
                );

            case 'confirm':
            default:
                return (
                    <>
                        <Typography variant="h5" sx={{ fontFamily: '"Orbitron", sans-serif', marginBottom: '12px' }}>
                            Unsubscribe from product updates
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                            Stop sending product updates to {emailMasked || 'this address'}? Account and
                            security emails will still be sent; they are part of your account.
                        </Typography>
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={processing}
                            onClick={handleUnsubscribe}
                            sx={{ marginTop: '24px', padding: '12px' }}
                        >
                            Unsubscribe
                            {processing && (
                                <CircularProgress size={20} sx={{ marginLeft: '10px', color: colors.bg.primary }} />
                            )}
                        </Button>
                    </>
                );
        }
    };

    return (
        <Container sx={{ marginTop: '120px', marginBottom: '60px' }} disableGutters maxWidth="sm" className="page-enter">
            <AuthBrandHeader />
            <GlowCard gradientBorder sx={{ padding: '40px 32px' }}>
                <Box sx={{ textAlign: 'center' }}>{body()}</Box>
            </GlowCard>
        </Container>
    );
};

export default Unsubscribe;
